import React, { useState } from 'react';
import { motion } from 'framer-motion';
import headshotImage from '../assets/Connor_Madigan_Headshot.jpeg';
import runningImage from '../assets/Madison_Half_Marathon.JPG';
import kidCudiMoonCover from '../assets/albums/Kid_Cudi_TheBoyWhoFlewToTheMoon.webp';
import manOnTheMoonCover from '../assets/albums/man_on_the_moon_kid_cudi.jpeg';
import swimmingCover from '../assets/albums/swimming_mac_miller.jpeg';
import beatlesCover from '../assets/albums/The_Beatles_Cover.png';
import killersCover from '../assets/albums/The_Killers_Hot_Fuss.png';
import dominicFikeCover from '../assets/albums/what_could_possibly_go_wrong_dominic_fike.jpeg';

const AboutRetroDual = () => {
  const [selectedSemester, setSelectedSemester] = useState('Spring 2025');

  const coursework = {
    'Spring 2025': {
      credits: 13,
      courses: [
        'Engineering Measurements & Instrumentation',
        'Energy Systems Lab',
        'Intro to Comparative US Ethnic & American Indian Studies',
        'Senior Design Project',
      ],
    },
    'Fall 2024': {
      credits: 17,
      courses: [
        'Design of Machine Elements',
        'Manufacturing: Metals & Automation',
        'Engineering Communications',
        'Data Programming II',
        'Senior Design Project',
        'Music in Performance',
      ],
    },
    'Summer 2024': {
      credits: 3,
      courses: [
        'Principles of Microeconomics',
      ],
    },
    'Spring 2024 (Abroad)': {
      credits: 15,
      courses: [
        'Elementary Heat Transfer',
        'Electrical & Electronic Circuits',
        'Intro to Human-Computer Interaction',
        'Production & Service Systems',
        'The Irish Tradition',
      ],
    },
    'Fall 2023 (Co-op)': {
      credits: 4,
      courses: [
        'Fluid Dynamics',
        'Cooperative Education Program',
      ],
    },
    'Spring 2023': {
      credits: 17,
      courses: [
        'Thermodynamics',
        'Dynamic Systems',
        'Computer-Aided Engineering',
        'Manufacturing: Polymers',
        'Programming III',
        'Mechanics of Materials Lab',
        'Music in Performance',
      ],
    },
    'Fall 2022': {
      credits: 16,
      courses: [
        'Dynamics',
        'Mechanics of Materials',
        'Intro to Material Science',
        'Programming II',
        'Intro to Computer Engineering',
        'Music in Performance',
      ],
    },
    'Spring 2022': {
      credits: 15,
      courses: [
        'Statics',
        'Linear Algebra & Differential Equations',
        'Geometric Modeling',
        'Applied Statistics for Engineers',
        'Programming I',
      ],
    },
    'Fall 2021': {
      credits: 14,
      courses: [
        'Introduction to Mechanical Engineering',
        'Multivariable Calculus',
        'General Chemistry',
        'Science & Storytelling',
      ],
    },
  };

  const technicalSkills = {
    'CAD & Design': { skills: ['SolidWorks', 'Creo Parametric', 'Onshape', 'Fusion 360', 'Geometric Modeling'], color: 'bg-blue-400 dark:bg-cyan-400' },
    'Programming': { skills: ['Python', 'Java', 'MATLAB', 'Arduino C++', 'VBA'], color: 'bg-green-400' },
    'Analysis': { skills: ['FEA', 'Thermal Analysis', 'AGMA Standards', 'GD&T', 'Statistical Analysis'], color: 'bg-orange-400 dark:bg-pink-400' },
    'Manufacturing': { skills: ['3D Printing', 'CNC Machining', 'Injection Molding', 'DFM', 'Assembly'], color: 'bg-purple-400' },
    'Software': { skills: ['Excel', 'Moldex3D', 'React.js', 'Git', 'Microsoft Office Suite'], color: 'bg-yellow-400' }
  };

  return (
    <div className="min-h-screen bg-[#F5E6D3] dark:bg-[#1a1a2e] relative overflow-hidden transition-colors duration-500">
      {/* Dot Grid Background - Light */}
      <div 
        className="absolute inset-0 opacity-20 dark:opacity-0 transition-opacity duration-500"
        style={{
          backgroundImage: `radial-gradient(circle, #8B7355 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }}
      />
      
      {/* Dot Grid Background - Dark (Glowing) */}
      <div 
        className="absolute inset-0 opacity-0 dark:opacity-30 transition-opacity duration-500"
        style={{
          backgroundImage: `radial-gradient(circle, #00ff88 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }}
      />

      {/* Scanline Effect - Dark Mode */}
      <div 
        className="absolute inset-0 opacity-0 dark:opacity-5 pointer-events-none transition-opacity duration-500"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, #00ff88 2px, #00ff88 4px)`
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 py-12">
        
        {/* Page Header */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block bg-white dark:bg-[#2d2d44] border-4 border-black dark:border-cyan-400 px-8 py-4 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(0,255,255,0.5)] mb-4 transition-all duration-500">
            <h1 className="text-5xl font-black uppercase tracking-tight transition-colors duration-500">
              ABOUT <span className="text-blue-600 dark:text-cyan-400 dark:drop-shadow-[0_0_20px_rgba(0,255,255,0.8)]">ME</span>
            </h1>
          </div>
          <p className="text-xl font-bold text-gray-800 dark:text-green-400 max-w-2xl mx-auto transition-colors duration-500">
            🎓 Mechanical Engineer | 💻 CAD Designer | 🔧 Problem Solver
          </p>
        </motion.div>

        {/* Main Bio Window */}
        <motion.div
          className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-pink-400 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(255,0,255,0.5)] mb-8 transition-all duration-500"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Window Title Bar */}
          <div className="bg-gradient-to-r from-blue-400 to-cyan-500 dark:from-pink-500 dark:to-purple-600 border-b-4 border-black dark:border-pink-400 px-4 py-3 flex items-center justify-between transition-all duration-500">
            <div className="flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-black dark:shadow-[0_0_10px_rgba(255,0,0,0.8)]"></div>
                <div className="w-4 h-4 rounded-full bg-yellow-300 border-2 border-black dark:shadow-[0_0_10px_rgba(255,255,0,0.8)]"></div>
                <div className="w-4 h-4 rounded-full bg-green-400 border-2 border-black dark:shadow-[0_0_10px_rgba(0,255,0,0.8)]"></div>
              </div>
              <span className="font-black text-lg uppercase tracking-tight ml-2 text-black dark:text-pink-300 transition-colors duration-500">MY_STORY.txt</span>
            </div>
          </div>

          {/* Bio Content */}
          <div className="p-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-2/3 space-y-4 font-bold text-gray-800 dark:text-gray-300 leading-relaxed text-justify transition-colors duration-500">
                <p>
                  I graduated with a degree in Mechanical Engineering from the University of Wisconsin-Madison in May 2025, complemented by a Certificate in Computer Science. This combination has given me a strong foundation in both mechanical design principles and programming fundamentals. My academic journey also took me abroad to the University of Limerick in Ireland, where I gained valuable cross-cultural insights and a broader perspective on engineering solutions. I'm currently pursuing a part-time online Master's degree in Mechanical Engineering at Purdue University to continue expanding my technical expertise.
                </p>
                <p>
                  Professionally, I work as a Product Engineer at MISUMI, where I process custom manufacturing quotes, coordinate with international suppliers, and serve as a technical liaison between customers and manufacturing teams. Prior to this, I gained diverse hands-on experience through internships and co-ops at companies like Acciona Energia and Extreme Engineering Solutions, where I focused on quality control for renewable energy projects and designed rugged embedded computing systems. These experiences have emphasized the importance of collaboration, attention to detail, and applying Design for Manufacturability (DFM) principles to ensure seamless production workflows.
                </p>
                <p>
                  Outside of work and school, I stay active with running—I've completed a couple of half-marathons and occasionally train for longer distances when I'm feeling ambitious. I'm a devoted Chicago Bears and Wisconsin Badgers fan. I also enjoy working on side projects that blend engineering and programming, like building tools to optimize decisions or automate tasks. Most importantly, I value spending quality time with friends and family.
                </p>
              </div>
              
              {/* Images */}
              <div className="lg:w-1/3 flex flex-col gap-4 justify-center">
                <motion.div
                  className="border-4 border-black dark:border-cyan-400 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(0,255,255,0.5)] transition-all duration-500 overflow-hidden"
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={headshotImage} 
                    alt="Connor Madigan" 
                    className="w-full h-52 object-cover object-[center_20%] block"
                  />
                </motion.div>
                <motion.div
                  className="border-4 border-black dark:border-pink-400 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(255,0,255,0.5)] transition-all duration-500 overflow-hidden"
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={runningImage}
                    alt="Madison Half Marathon"
                    className="w-full h-72 object-cover object-[center_30%] block"
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Technical Skills Window */}
        <motion.div
          className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-cyan-400 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(0,255,255,0.5)] mb-8 transition-all duration-500"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {/* Window Title Bar */}
          <div className="bg-gradient-to-r from-orange-400 to-red-500 dark:from-cyan-400 dark:to-purple-500 border-b-4 border-black dark:border-cyan-400 px-4 py-3 flex items-center justify-between transition-all duration-500">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-black dark:bg-cyan-400 dark:shadow-[0_0_10px_rgba(0,255,255,0.8)]"></div>
              <span className="font-black text-lg uppercase tracking-tight text-black dark:text-cyan-300 transition-colors duration-500">SKILLS.exe</span>
            </div>
            <div className="flex gap-1">
              <div className="w-5 h-5 bg-white dark:bg-[#1a1a2e] border-2 border-black dark:border-cyan-400 flex items-center justify-center dark:text-cyan-300 transition-all duration-500">
                <span className="text-xs font-black">_</span>
              </div>
            </div>
          </div>

          {/* Skills Grid */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(technicalSkills).map(([category, data], index) => (
                <motion.div
                  key={category}
                  className="bg-white dark:bg-[#1a1a2e] border-4 border-black dark:border-cyan-400 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(0,255,255,0.5)] transition-all duration-500"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                >
                  {/* Category Header */}
                  <div className={`${data.color} border-b-4 border-black dark:border-cyan-400 px-4 py-2 transition-all duration-500`}>
                    <h3 className="font-black uppercase text-sm tracking-tight text-black">{category}</h3>
                  </div>
                  {/* Skills */}
                  <div className="p-4 flex flex-wrap gap-2">
                    {data.skills.map((skill, skillIndex) => (
                      <span
                        key={skillIndex}
                        className="px-3 py-1 bg-gray-200 dark:bg-[#2d2d44] border-2 border-black dark:border-cyan-400 text-xs font-black uppercase hover:bg-gray-300 dark:hover:bg-cyan-400 dark:hover:text-black transition-colors text-black dark:text-cyan-300 duration-500"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Coursework Window */}
          <motion.div
            className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-green-400 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(0,255,0,0.5)] transition-all duration-500"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {/* Window Title Bar */}
            <div className="bg-gradient-to-r from-green-400 to-emerald-500 border-b-4 border-black dark:border-green-400 px-4 py-3 flex items-center justify-between transition-all duration-500">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-black dark:bg-green-400 dark:shadow-[0_0_10px_rgba(0,255,0,0.8)]"></div>
                <span className="font-black text-lg uppercase tracking-tight text-black dark:text-green-300 transition-colors duration-500">📚 COURSEWORK</span>
              </div>
            </div>

            {/* Coursework Content */}
            <div className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Semester Selector */}
                <div className="md:w-2/5 space-y-2 max-h-80 overflow-y-auto">
                  {Object.keys(coursework).map((semester) => (
                    <button
                      key={semester}
                      onClick={() => setSelectedSemester(semester)}
                      className={`w-full py-2 px-3 border-3 border-black dark:border-green-400 text-left text-xs font-black uppercase transition-all duration-500 ${
                        selectedSemester === semester 
                          ? 'bg-yellow-400 shadow-[inset_2px_2px_0px_0px_rgba(0,0,0,0.3)] dark:shadow-[0_0_15px_rgba(255,255,0,0.8)]' 
                          : 'bg-white dark:bg-[#1a1a2e] hover:bg-gray-100 dark:hover:bg-green-400 dark:hover:text-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[2px_2px_0px_0px_rgba(0,255,0,0.5)] dark:text-green-300'
                      }`}
                    >
                      {semester}
                    </button>
                  ))}
                </div>
                
                {/* Course Details */}
                <div className="md:w-3/5">
                  <motion.div
                    key={selectedSemester}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white dark:bg-[#1a1a2e] border-3 border-black dark:border-green-400 p-4 transition-all duration-500"
                  >
                    <h3 className="font-black uppercase text-sm mb-2 dark:text-white transition-colors duration-500">{selectedSemester}</h3>
                    <div className="bg-blue-400 dark:bg-green-400 border-2 border-black px-2 py-1 inline-block mb-3">
                      <span className="font-black text-xs text-black">CREDITS: {coursework[selectedSemester]?.credits}</span>
                    </div>
                    <div className="space-y-1">
                      {coursework[selectedSemester]?.courses.map((course, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-2 text-xs font-bold dark:text-gray-300 transition-colors duration-500"
                        >
                          <div className="w-2 h-2 bg-black dark:bg-green-400 mt-1 flex-shrink-0"></div>
                          <span>{course}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Music Window */}
          <motion.div
            className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-purple-400 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(138,43,226,0.5)] transition-all duration-500"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {/* Window Title Bar */}
            <div className="bg-gradient-to-r from-purple-400 to-pink-500 border-b-4 border-black dark:border-purple-400 px-4 py-3 flex items-center justify-between transition-all duration-500">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-black dark:bg-purple-400 dark:shadow-[0_0_10px_rgba(138,43,226,0.8)]"></div>
                <span className="font-black text-lg uppercase tracking-tight text-black dark:text-purple-300 transition-colors duration-500">🎵 MY TUNES</span>
              </div>
              <div className="flex gap-2">
                <div className="w-6 h-6 bg-white dark:bg-[#1a1a2e] border-2 border-black dark:border-purple-400 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-purple-400 dark:hover:text-black cursor-pointer dark:text-purple-300 transition-all duration-300">
                  <span className="font-black text-xs">▶</span>
                </div>
              </div>
            </div>

            {/* Album Grid */}
            <div className="p-6">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { src: kidCudiMoonCover, alt: "Kid Cudi - The Boy Who Flew to the Moon" },
                  { src: manOnTheMoonCover, alt: "Kid Cudi - Man on the Moon" },
                  { src: swimmingCover, alt: "Mac Miller - Swimming" },
                  { src: beatlesCover, alt: "The Beatles" },
                  { src: killersCover, alt: "The Killers - Hot Fuss" },
                  { src: dominicFikeCover, alt: "Dominic Fike - What Could Possibly Go Wrong" }
                ].map((album, index) => (
                  <motion.div
                    key={index}
                    className="aspect-square border-4 border-black dark:border-purple-400 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(138,43,226,0.5)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[0_0_20px_rgba(138,43,226,0.8)] transition-all cursor-pointer overflow-hidden duration-500"
                    whileHover={{ scale: 1.05, rotate: 3 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img 
                      src={album.src} 
                      alt={album.alt}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-32 right-20 w-16 h-16 bg-blue-400 dark:bg-cyan-400 border-4 border-blue-600 dark:border-cyan-400 rotate-45 hidden lg:block dark:shadow-[0_0_30px_rgba(0,255,255,0.6)] dark:animate-pulse transition-all duration-500"></div>
      <div className="absolute bottom-32 left-20 w-12 h-12 bg-yellow-400 border-4 border-yellow-600 dark:border-yellow-400 hidden lg:block dark:shadow-[0_0_30px_rgba(255,255,0,0.6)] dark:animate-pulse transition-all duration-500"></div>
    </div>
  );
};

export default AboutRetroDual;